import { LoginRequestSchema, describeError, toAppError, zodIssuesToFieldErrors } from '@kdk/shared';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { lazy, Suspense, useRef, useState, type SyntheticEvent } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router';
import { z } from 'zod';
import { useAuth } from '../auth/AuthProvider';
import { safeRedirectPath } from '../auth/safeRedirectPath';
import { LoadingScreen } from './LoadingScreen';

type FieldName = 'id' | 'password';
type FieldErrors = Partial<Record<FieldName, string>>;

const ExpiredStateSchema = z.object({ reason: z.literal('expired') });

const firstMessages = (errors: Readonly<Record<string, readonly string[]>>): FieldErrors => ({
  ...(errors['id']?.[0] === undefined ? {} : { id: errors['id'][0] }),
  ...(errors['password']?.[0] === undefined ? {} : { password: errors['password'][0] }),
});

/** 演示アカウント欄はモック有効のビルドだけ。無効なら import ごと除去される。 */
const DemoAccountsPanel = __MOCK_ENABLED__ ? lazy(() => import('./DemoAccountsPanel')) : null;

export const LoginPage = () => {
  const auth = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [id, setId] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  if (auth.status === 'loading') return <LoadingScreen shape="login" />;
  if (auth.status === 'authenticated') {
    return <Navigate to={safeRedirectPath(searchParams.get('redirect'))} replace />;
  }

  const expired = ExpiredStateSchema.safeParse(location.state).success;

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    if (inFlight.current) return;
    const parsed = LoginRequestSchema.safeParse({ id, password });
    if (!parsed.success) {
      setFieldErrors(firstMessages(zodIssuesToFieldErrors(parsed.error.issues)));
      setFormError(null);
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setFormError(null);
    try {
      await auth.login(parsed.data);
    } catch (error) {
      const appError = toAppError(error);
      setFieldErrors(firstMessages(appError.fieldErrors ?? {}));
      setFormError(describeError(appError));
      setPassword('');
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    // ログイン画面は AppShell の外にあるので、この要素が唯一の main ランドマークになる
    <Box component="main" sx={{ minHeight: '100%', display: 'grid', placeItems: 'center', p: 2 }}>
      <Stack spacing={2} sx={{ width: 'min(420px, 100%)' }}>
        <Paper
          variant="outlined"
          component="form"
          noValidate
          onSubmit={(event: SyntheticEvent) => {
            void submit(event);
          }}
          sx={{ p: '28px' }}
        >
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>情報通信部門</Typography>
          <Typography variant="h1" component="h1" sx={{ mb: '18px' }}>
            ログイン
          </Typography>
          <Stack spacing={2}>
            {expired && (
              <Alert severity="info" role="status" aria-label="セッション切れ">
                セッションの有効期限が切れました。もう一度ログインしてください。
              </Alert>
            )}
            {formError !== null && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="ID"
              value={id}
              onChange={(event) => {
                setId(event.target.value);
              }}
              error={fieldErrors.id !== undefined}
              helperText={fieldErrors.id}
              autoComplete="username"
              autoFocus
              size="small"
              fullWidth
            />
            <TextField
              label="パスワード"
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              error={fieldErrors.password !== undefined}
              helperText={fieldErrors.password}
              autoComplete="current-password"
              size="small"
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={submitting}>
              ログイン
            </Button>
          </Stack>
        </Paper>

        {DemoAccountsPanel !== null && (
          <Suspense fallback={null}>
            <DemoAccountsPanel
              onPick={(account) => {
                setId(account.id);
                setPassword(account.password);
                setFieldErrors({});
              }}
            />
          </Suspense>
        )}
      </Stack>
    </Box>
  );
};
